/**
 * 应用上下文 - 全局状态管理
 */

import React, { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react';
import type { AppState, SOSRecord, SOSFlowState, GeoLocation, CapturedPhoto, SuspectedCaseType } from '../types';
import * as storage from '../services/storage';
import * as syncService from '../services/sync';

// 动作类型
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FLOW_STATE'; payload: SOSFlowState }
  | { type: 'SET_CURRENT_RECORD'; payload: SOSRecord | null }
  | { type: 'SET_RECORDS'; payload: SOSRecord[] }
  | { type: 'ADD_RECORD'; payload: SOSRecord }
  | { type: 'UPDATE_RECORD'; payload: SOSRecord }
  | { type: 'DELETE_RECORD'; payload: string }
  | { type: 'SET_LOCATION'; payload: GeoLocation | null }
  | { type: 'SET_PHOTO'; payload: CapturedPhoto | null }
  | { type: 'RESET_SOS_FLOW' };

// 初始状态
const initialState: AppState & {
  flowState: SOSFlowState;
  currentLocation: GeoLocation | null;
  currentPhoto: CapturedPhoto | null;
} = {
  currentRecord: null,
  records: [],
  isLoading: false,
  error: null,
  flowState: 'idle',
  currentLocation: null,
  currentPhoto: null
};

// Reducer
function appReducer(
  state: typeof initialState,
  action: AppAction
): typeof initialState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_FLOW_STATE':
      return { ...state, flowState: action.payload };
    case 'SET_CURRENT_RECORD':
      return { ...state, currentRecord: action.payload };
    case 'SET_RECORDS':
      return { ...state, records: action.payload };
    case 'ADD_RECORD':
      return { ...state, records: [action.payload, ...state.records] };
    case 'UPDATE_RECORD':
      return {
        ...state,
        records: state.records.map(r =>
          r.id === action.payload.id ? action.payload : r
        )
      };
    case 'DELETE_RECORD':
      return {
        ...state,
        records: state.records.filter(r => r.id !== action.payload)
      };
    case 'SET_LOCATION':
      return { ...state, currentLocation: action.payload };
    case 'SET_PHOTO':
      return { ...state, currentPhoto: action.payload };
    case 'RESET_SOS_FLOW':
      return {
        ...state,
        flowState: 'idle',
        currentLocation: null,
        currentPhoto: null,
        currentRecord: null,
        error: null
      };
    default:
      return state;
  }
}

// Context
interface AppContextValue {
  state: typeof initialState;
  dispatch: React.Dispatch<AppAction>;
  loadRecords: () => Promise<void>;
  triggerSOS: () => Promise<void>;
  saveSOSRecord: (location: GeoLocation, photo: CapturedPhoto | null, description: string) => Promise<SOSRecord>;
  syncRecord: (record: SOSRecord) => Promise<boolean>;
  deleteRecord: (id: string) => Promise<void>;
  clearAllData: () => Promise<void>;
  saveSuspectedReport: (data: {
    caseType: SuspectedCaseType;
    photos: string[];
    description: string;
    witnessConsent: boolean;
  }) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

// Provider组件
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // 加载历史记录
  const loadRecords = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const records = await storage.getAllSOSRecords();
      dispatch({ type: 'SET_RECORDS', payload: records });
    } catch (error) {
      console.error('[应用] 加载记录失败:', error);
      dispatch({ type: 'SET_ERROR', payload: '加载历史记录失败' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // 触发SOS流程
  const triggerSOS = async () => {
    dispatch({ type: 'SET_FLOW_STATE', payload: 'requesting_permission' });
  };

  // 保存SOS记录
  const saveSOSRecord = async (
    location: GeoLocation,
    photo: CapturedPhoto | null,
    description: string
  ): Promise<SOSRecord> => {
    dispatch({ type: 'SET_FLOW_STATE', payload: 'saving' });

    const { encryptLocation, encryptPhoto, encryptData } = await import('../services/encryption');

    const encryptedLocation = await encryptLocation(location);

    const recordData: {
      encryptedLocation: string;
      encryptedLocationIV: string;
      encryptedPhoto?: string;
      encryptedPhotoIV?: string;
      encryptedDescription?: string;
      encryptedDescriptionIV?: string;
      deviceInfo: string;
      appVersion: string;
    } = {
      encryptedLocation: encryptedLocation.ciphertext,
      encryptedLocationIV: encryptedLocation.iv,
      deviceInfo: `${navigator.platform} - ${navigator.userAgent.slice(0, 50)}`,
      appVersion: '1.0.0'
    };

    if (photo) {
      const encryptedPhoto = await encryptPhoto(photo);
      recordData.encryptedPhoto = encryptedPhoto.ciphertext;
      recordData.encryptedPhotoIV = encryptedPhoto.iv;
    }

    if (description) {
      const encryptedDesc = await encryptData(description);
      recordData.encryptedDescription = encryptedDesc.ciphertext;
      recordData.encryptedDescriptionIV = encryptedDesc.iv;
    }

    const record = await storage.addSOSRecord(recordData);

    dispatch({ type: 'ADD_RECORD', payload: record });
    dispatch({ type: 'SET_CURRENT_RECORD', payload: record });

    return record;
  };

  // 同步记录
  const syncRecord = async (record: SOSRecord): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_FLOW_STATE', payload: 'syncing' });

      const result = await syncService.reportEmergency(record);

      if (result.success) {
        await storage.updateSyncStatus(record.id, 'synced');
        dispatch({
          type: 'UPDATE_RECORD',
          payload: { ...record, syncStatus: 'synced' }
        });
        return true;
      } else {
        await storage.updateSyncStatus(record.id, 'failed', true);
        dispatch({
          type: 'UPDATE_RECORD',
          payload: { ...record, syncStatus: 'failed', syncAttempts: record.syncAttempts + 1 }
        });
        // 注册后台同步
        await syncService.registerBackgroundSync(record.id);
        return false;
      }
    } catch (error) {
      console.error('[应用] 同步失败:', error);
      await storage.updateSyncStatus(record.id, 'failed', true);
      return false;
    }
  };

  // 删除记录
  const deleteRecord = async (id: string) => {
    await storage.deleteSOSRecord(id);
    dispatch({ type: 'DELETE_RECORD', payload: id });
  };

  // 清除所有数据
  const clearAllData = async () => {
    await storage.clearAllData();
    dispatch({ type: 'SET_RECORDS', payload: [] });
  };

  // 保存可疑上报
  const saveSuspectedReport = async (data: {
    caseType: SuspectedCaseType;
    photos: string[];
    description: string;
    witnessConsent: boolean;
  }) => {
    const { encryptData } = await import('../services/encryption');

    // 加密照片（多张）
    const photosJson = JSON.stringify(data.photos);
    const encryptedPhotos = await encryptData(photosJson);

    // 加密描述
    const encryptedDesc = await encryptData(data.description);

    // 获取位置
    let location: {
      latitude: number;
      longitude: number;
      accuracy: number;
      altitude: number | null;
      timestamp: number;
      provider: 'gps' | 'network' | 'unknown';
    } = {
      latitude: 0,
      longitude: 0,
      accuracy: -1,
      altitude: null,
      timestamp: Date.now(),
      provider: 'unknown'
    };
    try {
      const { getCurrentLocation } = await import('../services/location');
      const result = await getCurrentLocation(false, 5000);
      location = result;
    } catch (e) {
      console.log('[应用] 获取位置失败，使用默认值');
    }

    const encryptedLocation = await encryptData(JSON.stringify(location));

    await storage.addSuspectedReport({
      caseType: data.caseType,
      encryptedLocation: encryptedLocation.ciphertext,
      encryptedLocationIV: encryptedLocation.iv,
      encryptedPhotos: encryptedPhotos.ciphertext,
      encryptedPhotosIV: encryptedPhotos.iv,
      encryptedDescription: encryptedDesc.ciphertext,
      encryptedDescriptionIV: encryptedDesc.iv,
      witnessConsent: data.witnessConsent,
      appVersion: '1.0.0'
    });
  };

  // 初始化时加载记录
  useEffect(() => {
    loadRecords();
  }, []);

  const value: AppContextValue = {
    state,
    dispatch,
    loadRecords,
    triggerSOS,
    saveSOSRecord,
    syncRecord,
    deleteRecord,
    clearAllData,
    saveSuspectedReport
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp必须在AppProvider内使用');
  }
  return context;
}
